'use client'
import { toast } from '@/components/common/toast'
import { ToastAddToCartData } from '@/components/common/toast/toast-cart'
import { DeviceTokenHelper } from '@/lib/helpers/device-token.helper'
import { __cCartService } from '@/services/client/__c.cart.service'
import {
  AddMultipleToCartParams,
  AddToCartParams,
  ChangeCartItemQuantityParams,
  ChangeWishlistItemQuantityParams,
  RemoveItemParams,
  UpdateCartItemParams,
  UpdateWishlistItemParams,
} from '@/types/cart'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '../constants'

import { AddToWishlistParams, RemoveWishlistItemParams } from '@/types/wishlist'
import { useIsLoggedIn } from '../user/use-user'

// =================== QUERY ===================

export function useCartQuery() {
  const isLoggedIn = useIsLoggedIn()

  return useQuery({
    queryKey: [QUERY_KEYS.CART, isLoggedIn],
    queryFn: () => __cCartService.getCart({ isLoggedIn }),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  })
}

export function useCartMiniQuery({ enabled }: { enabled: boolean }) {
  return useQuery({
    queryKey: [QUERY_KEYS.CART_MINI],
    queryFn: __cCartService.getCartMini,
    enabled,
  })
}

export function useCountCartItemQuery() {
  return useQuery({
    queryKey: [QUERY_KEYS.COUNT_CART_ITEM],
    queryFn: __cCartService.countCartItem,
  })
}

// =================== MUTATION ===================

export function useAddToCart() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      payload,
      toastOptions,
    }: {
      payload: Omit<AddToCartParams, 'device_token'>
      toastOptions?: ToastAddToCartData
    }) => {
      const fullPayload = {
        ...payload,
        device_token: DeviceTokenHelper.get(),
      }

      return __cCartService.addToCart(fullPayload, toastOptions)
    },
    onSuccess: (toastOptions) => {
      if (toastOptions) {
        toast.addToCartSuccess(toastOptions)
      }
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.COUNT_CART_ITEM,
        QUERY_KEYS.PROMOTION_PRODUCTS,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

      // Invalidate voucher queries to check voucher status
      queryClient.invalidateQueries({ queryKey: ['voucher-in-cart'] })
      queryClient.invalidateQueries({ queryKey: ['voucher-only-wallet'] })
    },
    onError: (error) => {
      if (error instanceof Error) {
        toast.message({
          title: 'Rất tiếc',
          message: error?.message || 'Lỗi khi thêm vào giỏ hàng',
        })
      }
      console.error(error)
    },
  })
}

export function useAddMultipleToCart() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, AddMultipleToCartParams, unknown>({
    mutationFn: (payload) =>
      __cCartService.addMultipleToCart({
        ...payload,
        device_token: DeviceTokenHelper.get(),
      }),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.WISHLIST,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, RemoveItemParams, unknown>({
    mutationFn: (payload: RemoveItemParams) =>
      __cCartService.removeItem(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.COUNT_CART_ITEM,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
        QUERY_KEYS.PROMOTION_PRODUCTS,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}
// Dùng chung với cart
export function useRemoveCartMiniItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, RemoveItemParams, unknown>({
    mutationFn: (payload: RemoveItemParams) =>
      __cCartService.removeItem(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.COUNT_CART_ITEM,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.PROMOTION_PRODUCTS,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

export function useRemoveAllCart() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, void, unknown>({
    mutationFn: () => __cCartService.removeAllCart(),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.COUNT_CART_ITEM,
        QUERY_KEYS.WISHLIST,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

export function useWishlistQuery() {
  return useQuery({
    queryKey: ['wishlist'],
    queryFn: () => __cCartService.getWishlist(),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  })
}

// =================== MUTATION ===================
export function useAddToWishlist() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, AddToWishlistParams, unknown>({
    mutationFn: (payload) => __cCartService.addToWishlist(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.WISHLIST,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

export function useRemoveWishlistItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, RemoveWishlistItemParams, unknown>({
    mutationFn: (payload: RemoveWishlistItemParams) =>
      __cCartService.removeWishlistItem(payload),
    onSuccess: () => {
      ;[QUERY_KEYS.WISHLIST].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      )
    },
  })
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, UpdateCartItemParams, unknown>({
    mutationFn: (payload) => __cCartService.updateCartItem(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

export function useUpdateWishlistItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, UpdateWishlistItemParams, unknown>({
    mutationFn: (payload) => __cCartService.updateWishlistItem(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WISHLIST] })
    },
  })
}

export function useUpdateCartItemQuantity() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, ChangeCartItemQuantityParams, unknown>({
    mutationFn: (payload) => __cCartService.updateCartItemQuantity(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

export function useUpdateWishlistItemQuantity() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, ChangeWishlistItemQuantityParams, unknown>(
    {
      mutationFn: (payload) =>
        __cCartService.updateWishlistItemQuantity(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WISHLIST] })
      },
    }
  )
}

// =================== COOLCASH ===================
export function useApplyCoolCash() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, number, unknown>({
    mutationFn: (payload) => __cCartService.applyCoolCash(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

export function usePromotionProductsQuery() {
  return useQuery({
    queryKey: [QUERY_KEYS.PROMOTION_PRODUCTS],
    queryFn: () => __cCartService.getPromotionProducts(),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  })
}
